import { GitMergeIcon, GitPullRequestIcon, LockIcon, RepoIcon, StackIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { Link } from "react-router-dom";
import { useRepos, useWhoami } from "../api/hooks";
import { ACTIVE_STATES, openCount, type RepoInfo, type RepoRow } from "../api/types";
import { Empty, Page, PageHead, Query, TimeAgo } from "../components/common";
import { bytes, short } from "../components/format";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

const landed = (info: RepoInfo | null) => (info?.counts.landed ?? 0) + (info?.counts.verified ?? 0);
const inFlight = (info: RepoInfo | null) => (info ? ACTIVE_STATES.reduce((n, s) => n + (info.counts[s] ?? 0), 0) : 0);

export type RepoWithInfo = RepoRow & { info: RepoInfo | null };

export function Explore() {
  const q = useRepos();
  const admin = !!useWhoami().data?.actions.includes("admin");
  return (
    <Page narrow>
      <PageHead
        title="Repositories"
        actions={
          admin && (
            <Button asChild variant="primary">
              <Link to="/new">
                <RepoIcon /> New
              </Link>
            </Button>
          )
        }
      >
        Agents open snapshots and run <code>bee land</code>; the server tests, rebases, lands and verifies.
      </PageHead>
      <Query q={q}>
        {(repos) =>
          repos.length === 0 ? (
            <Card>
              <Empty icon={RepoIcon} bee="idle">
                No repositories yet. Create one with <code>bee admin repo-create &lt;name&gt;</code> or import with <code>bee import &lt;git url or dir&gt;</code>.
              </Empty>
            </Card>
          ) : (
            <RepoList repos={repos} />
          )
        }
      </Query>
    </Page>
  );
}

/** One row per repository: name, state, description and activity counts. */
export function RepoList({ repos }: { repos: RepoWithInfo[] }) {
  return (
    <ul>
      {repos.map((r) => {
        const flight = inFlight(r.info);
        return (
          <li key={r.name} className="border-t border-border-muted py-3 first:border-t-0">
            <div className="flex items-center gap-2">
              <Link className="text-base font-semibold" to={rp(r.slug, r.owner)}>
                {r.slug}
              </Link>
              {r.info?.frozen ? (
                <Badge tone="red">
                  <LockIcon size={12} /> Frozen
                </Badge>
              ) : flight ? (
                <Badge>{flight} in flight</Badge>
              ) : null}
            </div>
            {r.description && <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{r.description}</p>}
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1">
              <span>
                <GitPullRequestIcon size={12} /> {r.info ? openCount(r.info.counts) : 0} open
              </span>
              <span>
                <GitMergeIcon size={12} /> {landed(r.info)} landed
              </span>
              <span>
                <StackIcon size={12} /> {bytes(r.info?.bundles.bytes ?? 0)}
              </span>
              {r.info?.main ? (
                <span className="font-mono">main@{short(r.info.main.commit_sha, 7)}</span>
              ) : r.import_state === "pending" ? (
                <span>importing…</span>
              ) : r.import_state === "failed" ? (
                <span className="text-danger">import failed</span>
              ) : (
                <span>empty</span>
              )}
              {r.info?.last && (
                <span>
                  Updated <TimeAgo ms={r.info.last.ts} />
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
